import type { SocketIn } from "~/js/graph/node-socket-connection";
import type { nodeNewExecutor } from "~/js/utilities/changeNodeExecutorOnCustom";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { Default_Executor_Text, onError } from "~/js/utilities/executor";
import { $div } from "~/js/utilities/TurboDom";
import { getPublicMessageFromError, isCancelError } from "~/solidJs/shared/helpers";
import { resizeImageWithCanvas } from "~/solidJs/shared/helpers/imageHelpers";
import { collectNodeDataForTask, findSocket } from "~/solidJs/shared/helpers/nodeHelpers";
import { getImageDimensionsFromFile } from "~/solidJs/shared/helpers/validateImages";
import { ExecutionStatus, NodeExecutor } from "../../node-executor";

export class SizeStatusSocket {
	widthText: HTMLDivElement;
	heightText: HTMLDivElement;
	statusEl: HTMLDivElement;
	statusLabel: HTMLDivElement;
	widthContainer: HTMLDivElement;
	width: HTMLDivElement;
	height: HTMLDivElement;

	constructor() {
		this.statusEl = $div("socket downscale hidden");
		this.statusLabel = $div("label-text", this.statusEl);
		this.widthContainer = $div("data-entry-container", this.statusEl);
		this.width = $div("downSampleCalc", this.widthContainer);
		this.height = $div("downSampleCalc", this.widthContainer);

		this.statusLabel.textContent = "Final resolution";
		this.width.textContent = "W";
		this.height.textContent = "H";
		this.widthText = $div("label-text", this.width);
		this.heightText = $div("label-text", this.height);
	}

	getDomElement() {
		return this.statusEl;
	}

	rerender(computedHeight: number, computedWidth: number) {
		this.widthText.textContent = `${computedWidth}`;
		this.heightText.textContent = `${computedHeight}`;
	}
}

const setSize = (height: number, width: number, socketX: SocketIn, socketY: SocketIn) => {
	socketX.setValue(height);
	socketY.setValue(width);
};
// Export the custom executor so it can be imported and used for cloud nodes
export const downScaleCustomExecutor: nodeNewExecutor = (node, _layout, _initialExecutor) => {
	const inputImg = node.inputSocketGroup.socketHashMap.get("img_input");

	if (!inputImg) {
		return;
	}
	const scalePercentParam = findSocket("scale_percent", node);
	const sizeX = findSocket("side_y", node);
	const sizeY = findSocket("side_x", node);
	if (!scalePercentParam || !sizeX || !sizeY) {
		throw new Error("Missing required parameters");
	}

	const executor = new NodeExecutor(node);
	executor.useEnterExitTriggers();
	executor.setStatusEnabled(true);
	executor.updateStatus(ExecutionStatus.Ready, Default_Executor_Text);
	executor.setExecuteFunction(async (_cancelController: AbortController) => {
		let interval: NodeJS.Timer;
		try {
			node?.eventor?.invoke("preExecutePreparing");
			const executionStartTs = Date.now();
			EventAnalytics.executeNode(node.globalId, executionStartTs);

			executor.DOMStyle.executeBlock.start();
			executor.updateStatus(ExecutionStatus.Computing, "Compute starting...");

			const _nodeData = await collectNodeDataForTask(node, node.executuionBlock);
			const img = await inputImg.tryGetValue();
			const height = await sizeX.getValue();
			const width = await sizeY.getValue();
			const file = await resizeImageWithCanvas(img, height, width);
			node.updateOutputs([
				{
					name: "img_resized",
					type: "image",
					value: [file]
				}
			]);
			const _startTime = Date.now();
			const _elapsedTime = 0;

			executor.updateStatus(ExecutionStatus.Done, "Successful");
			executor.DOMStyle.executeBlock.finish();
		} catch (e: unknown) {
			if (isCancelError(e)) return;

			onError(
				executor,
				getPublicMessageFromError(e) || "Unknown execution error",
				node,
				interval!,
				-1
			);
		}
	});
	let curHeight = 512;
	let curWidth = 512;
	const _image = new Image();
	let computedHeight = 512;
	let computedWidth = 512;
	let scalePercent = 100;

	const onImgChange = async () => {
		const value = await inputImg.tryGetValue();
		const sizes = await getImageDimensionsFromFile(value);
		if (sizes) {
			curHeight = sizes.height;
			curWidth = sizes.width;
			scalePercent = await scalePercentParam.getValue();
			computedHeight = scaleByPercentRound(curHeight, scalePercent);
			computedWidth = scaleByPercentRound(curWidth, scalePercent);

			setSize(computedHeight, computedWidth, sizeX, sizeY);
		}
	};
	inputImg.eventor.addListener("change", onImgChange);
	scalePercentParam.eventor.addListener("change", async () => {
		const percent = await scalePercentParam.tryGetValue();
		scalePercent = percent;
		computedHeight = scaleByPercentRound(curHeight, scalePercent);
		computedWidth = scaleByPercentRound(curWidth, scalePercent);
		setSize(computedHeight, computedWidth, sizeX, sizeY);
	});
	const handleRatioChange = async (
		input: SocketIn,
		originalValue: number,
		secondSocket: SocketIn,
		secondSocketOriginalValue: number
	) => {
		const value = await input.getValue();
		const percent = (value / originalValue) * 100;
		scalePercent = await scalePercentParam.setValue(percent);
		secondSocket.setValue(scaleByPercentRound(secondSocketOriginalValue, percent));
	};
	sizeX.eventor.addListener("change", async () => {
		handleRatioChange(sizeX, curHeight, sizeY, curWidth);
	});
	sizeY.eventor.addListener("change", async () => {
		handleRatioChange(sizeY, curWidth, sizeX, curHeight);
	});
};
const scaleByPercentRound = (number: number, percent: number) => {
	return Math.round(number * (percent / 100));
};
